import { useEffect, useState } from "react";

/**
 * Mobile breakpoint hook. A device is "mobile" when its viewport is at or below
 * `breakpoint` CSS pixels (default 767 — the top of the phone band; tablets and
 * the desktop/admin console get the full layout). Returns true during SSR/first
 * paint too, so the mobile shell can render immediately without a flash.
 *
 * Used by AdminHubPage to swap the dense desktop AdminPage for the mobile-only
 * AdminPageMobile without either component knowing about the other.
 */
export function useIsMobile(breakpoint = 767) {
  const query = `(max-width: ${breakpoint}px)`;
  const getMatch = () =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : true;

  const [isMobile, setIsMobile] = useState(getMatch);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = e => setIsMobile(e.matches);
    // Older Safari uses addListener; modern uses addEventListener.
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    setIsMobile(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return isMobile;
}

export default useIsMobile;
